/**
 * IDENTITY LAB — Code101 chapter 5, three questions that get smushed together.
 *
 * FOUNDER, 2026-08-14: "ch5 next".
 *
 * ── THE CHAPTER SAYS WHY IT MATTERS, SO THE LAB LEADS WITH THE PAYOFF ─────────────────────────
 * §2: "Mixing them up sounds like a small thing. It is not — it is what separates a literate user
 * from a confused one." And it gives the two sentences a learner will actually hear: "I got hacked"
 * is authentication; "the system leaked customer data" is authorization. Those two lines are the
 * take-away, so the lab's job is to make each of them something you WATCHED rather than read.
 *
 * ── THE DEMONSTRATION IS THE THIRD FAILURE, NOT THE FIRST ─────────────────────────────────────
 * Everyone has met a wrong password. Almost nobody has watched a correctly signed-in person be
 * handed a stranger's data. So the lab's centre of gravity is the authorization switch: leave it
 * on and the same request is refused; turn it off and Maya — properly identified, properly
 * authenticated, holding a valid token — is shown her neighbour's list. Nothing was hacked. The
 * system is broken. That gap is the chapter.
 *
 * ── WHAT IT REFUSES ───────────────────────────────────────────────────────────────────────────
 * There is no real authentication here and it says so: the password is printed on the screen. A lab
 * that made you guess would be teaching a password game, and a lab that pretended to be a real
 * login would be a phishing rehearsal. The accounts are example.com and the neighbour's data is
 * deliberately dull — the point of a leak is that it happened, not that it was interesting.
 *
 * OAuth (§4) and API keys (§6) are named and not modelled. Each is a further mechanism, and this
 * lab is the three questions plus the token that carries the answer between requests.
 */

import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  QUESTIONS, ACCOUNTS, SHOWN_PHRASE, attempt, IN_PLAIN_SPEECH, THE_POINT, WHY_IT_MATTERS,
  TOKEN_FACTS, STOLEN_DEVICE, NO_MEMORY, SOURCE_2, SOURCE_5,
} from './identity3.mjs';

export default function IdentityLab() {
  const [password, setPassword] = useState(SHOWN_PHRASE);
  const [opening, setOpening] = useState('maya');
  const [checks, setChecks] = useState(true);
  const [tried, setTried] = useState(false);
  const [tokenAlive, setTokenAlive] = useState(true);

  const r = attempt({ claimedAs: 'maya', password, checksOwnership: checks, opening });
  const neighbour = ACCOUNTS.find((a) => a.id === 'neighbour');

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Identity lab · three questions</div>
      <h2 className="g-h2 lab-h">“I got hacked” and “they leaked my data” are not the same sentence</h2>
      <p className="g-body">
        One of them means somebody got a password. The other means nobody needed one. Sign in as
        Maya below, then try to open a list that is not hers — with the ownership check on, and then
        with it off.
      </p>

      {/* ── THE THREE, ALWAYS VISIBLE ── */}
      <div className="idn-three">
        {QUESTIONS.map((q, i) => {
          const st = tried ? r.stages.find((s) => s.q === q.id) : null;
          return (
            <div key={q.id} className={`idn-q ${st ? (st.ok ? 'idn-ok' : 'idn-bad') : ''}`}>
              <div className="idn-qhead"><span className="cou-n">{i + 1}</span><b>{q.name}</b></div>
              <p className="g-body idn-asks">{q.asks}</p>
              <p className="g-body g-dim idn-eg">{q.eg}</p>
              {st && <p className="g-body idn-says">{st.ok ? '✓' : '✗'} {st.says}</p>}
            </div>
          );
        })}
      </div>

      {/* ── THE ATTEMPT ── */}
      <div className="lab-task">
        <div className="lab-readout">
          <div>
            <span className="g-label">Claiming to be</span>
            <span className="lab-num idn-mono">maya@example.com</span>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <span className="g-label">Proving it — the password is <b className="idn-mono">{SHOWN_PHRASE}</b></span>
            <input className="morse-input idn-pw" value={password}
              onChange={(e) => { setPassword(e.target.value); setTried(false); }} />
          </div>
        </div>

        <div className="lab-task-head">
          <span className="g-label">Open the grocery list of</span>
          {ACCOUNTS.map((a) => (
            <button key={a.id} className={`g-btn ${opening === a.id ? 'g-btn-primary' : ''}`}
              onClick={() => { setOpening(a.id); setTried(false); }}>{a.label}</button>
          ))}
        </div>

        <div className="lab-task-head">
          <label className="morse-check">
            <input type="checkbox" checked={checks}
              onChange={(e) => { setChecks(e.target.checked); setTried(false); }} />
            The system checks whose row this is
          </label>
          <button className="g-btn g-btn-primary" onClick={() => { setTried(true); setTokenAlive(true); }}>
            Sign in and open it
          </button>
        </div>
      </div>

      {/* ── WHAT CAME BACK ── */}
      {tried && (
        <div className="lab-task">
          <div className="g-label">What came back</div>

          {!r.signedIn && (
            <>
              <Value of={refused(r.stages.find((s) => !s.ok).says)} />
              <p className="g-body idn-diag">
                Stopped at <b>{r.failedAt}</b>. Nothing was shown, because the system never got past
                the question “can you prove it”.
              </p>
              <p className="g-body g-dim"><Value of={cited(IN_PLAIN_SPEECH.authentication, SOURCE_2)} /></p>
            </>
          )}

          {r.signedIn && !r.saw && (
            <>
              <Value of={refused('this row belongs to someone else')} />
              <p className="g-body idn-diag">
                Maya is signed in and her token is valid. She was still refused — that is
                <b> authorization</b> doing its job, and it is a different question from whether she
                is Maya.
              </p>
            </>
          )}

          {r.saw && (
            <>
              <div className={`idn-data ${r.leak ? 'idn-leak' : ''}`}>
                <div className="g-label">{r.leak ? `${neighbour.label}'s list — on Maya's screen` : 'Maya’s list'}</div>
                <ul>{r.saw.list.map((x) => <li key={x}>{x}</li>)}</ul>
              </div>
              {r.leak && (
                <>
                  <p className="g-body idn-diag">
                    <b>Nobody was hacked.</b> The password was right. The token is valid. Every one of
                    the three questions returned a tick — because the third one was never really
                    asked. This is a leak, and its name is <b>authorization</b>.
                  </p>
                  <p className="g-body g-dim"><Value of={cited(IN_PLAIN_SPEECH.authorization, SOURCE_2)} /></p>
                </>
              )}
            </>
          )}

          {/* ── THE TOKEN, §5 ── */}
          {r.token && (
            <div className="idn-token">
              <div className="g-label">The small piece of paper</div>
              <div className="lab-readout">
                <div><span className="g-label">Issued to</span>
                  <Value of={computed(r.token.forWhom)} /></div>
                <div><span className="g-label">State</span>
                  <span className="lab-num">{tokenAlive ? 'valid' : 'deleted'}</span></div>
              </div>
              <p className="g-body g-dim"><Value of={cited(NO_MEMORY, SOURCE_5)} /> The token is what
                the server reads on every later request to know the sign-in already happened.</p>
              <div className="lab-task-head">
                <button className="g-btn" onClick={() => setTokenAlive(false)} disabled={!tokenAlive}>
                  Sign out — delete the token
                </button>
                <button className="g-btn" onClick={() => { setTokenAlive(false); setTried(false); }}>
                  Sign out everywhere
                </button>
              </div>
              {!tokenAlive && <p className="g-body idn-diag">The token is gone. The password never changed —
                and the next request will be treated as a stranger's.</p>}
              <details className="cou-tr">
                <summary className="g-label">Four things this explains</summary>
                <dl className="cou-dl">
                  {TOKEN_FACTS.map((t) => (
                    <div key={t.id}><dt>{t.label}</dt><dd>{t.quote}</dd></div>
                  ))}
                </dl>
                <p className="g-body"><Value of={cited(STOLEN_DEVICE, SOURCE_5)} /></p>
              </details>
            </div>
          )}
        </div>
      )}

      <p className="g-body idn-punch">
        <Value of={cited(THE_POINT, SOURCE_2)} /> <Value of={cited(WHY_IT_MATTERS, SOURCE_2)} />
      </p>

      <p className="g-body g-dim lab-why">
        There is no real sign-in here — the password is printed above, because a lab that made you
        guess would be teaching a password game, and one that looked like a real login would be a
        rehearsal for being phished. The accounts are example.com and the neighbour's list is dull on
        purpose: what matters about a leak is that it happened, not what it said.
        <br />
        “Sign in with Google” (§4) and API keys (§6) are further mechanisms and are not modelled
        here. This lab is the three questions, and the piece of paper that carries the answer from
        one request to the next.
      </p>
    </div>
  );
}
